import pickBy from 'lodash-es/pickBy'
import { type as BatchType } from 'redux-batch-middleware'
import { getEnv } from 'aria-viewer/utils/environment'

if (!Array.isArray) {
    Array.isArray = function(arg) {
        return Object.prototype.toString.call(arg) === '[object Array]'
    }
}

/**
 * Filters out any properties which are not defined.
 */
const filterAnalyticProperties = properties => {
    const isNotEmpty = property => !['', null].includes(property)

    return pickBy(properties, isNotEmpty)
}

/**
 * Formats the track event as required by redux-segment.
 */
export const trackEvent = (event, properties) => {
    return {
        eventType: EventTypes.track,
        eventPayload: {
            event,
            properties: filterAnalyticProperties(properties),
        },
    }
}

/**
 * Formats the identify as required by redux-segment.
 */
export const identifyUser = (userkey, properties) => {
    return {
        eventType: EventTypes.identify,
        eventPayload: {
            userId: userkey,
            traits: filterAnalyticProperties(properties),
        },
    }
}

const emitEvents = ({ events, analytics }) => {
    const eventsToEmit = !Array.isArray(events) ? [events] : events

    eventsToEmit.forEach(({ eventType, eventPayload }) => {
        if (analytics[eventType]) {
            const { event, properties } = eventPayload

            analytics[eventType](event, properties)
        }
    })
}

const mapBatchedEvents = batchedEvents => (getState, action) => {
    let events = []

    action.payload.forEach(_action => {
        if (batchedEvents[_action.type]) events.push(batchedEvents[_action.type](getState, _action) || {})
    })

    return events
}

const trackerMiddleWare = ({ mapper, analytics, onPreProcess }) => ({ getState }) => next => action => {
    const mappedAction = mapper[action.type]

    const isBatchedAction = action.type == BatchType
    const hasMappedAction = !!mappedAction

    if (!hasMappedAction && !isBatchedAction) {
        return next(action)
    }

    let events

    if (isBatchedAction) {
        const batchedEvents = mapBatchedEvents(mapper)(getState, action)
        events = batchedEvents.length > 0 ? batchedEvents : undefined
    } else {
        events = mappedAction(getState, action)
    }

    if (events) {
        const preProcessedEvents = onPreProcess && onPreProcess(events, getState, action)

        if (preProcessedEvents != undefined) events = preProcessedEvents

        emitEvents({ events, analytics })
    }

    return next(action)
}

export const getAnalyticsMiddleware = Analytics => {
    if (!Analytics) return false

    if (Array.isArray(Analytics)) {
        return Analytics.map(analytic => createTracker(analytic))
    } else {
        return [createTracker(Analytics)]
    }
}

export const createTracker = ({ token, mapper, analytics, socketConfig = {}, onPreProcess }) => {
    if (!token || typeof token !== 'string') {
        throw new Error('Please provide Mixpanel Token')
    }

    //TODO: this logic needs to work well with dev, staging and prod
    if (getEnv('REACT_APP_ANALYTICS_WEBSOCKET_ENDPOINT') && analytics.name !== undefined) {
        const { socket, retries, delay, backOffCoefficient } = socketConfig

        analytics.init(token, {
            endpoint: getEnv('REACT_APP_ANALYTICS_WEBSOCKET_ENDPOINT'),
            onIntialized: _ => socket && socket.onopen && socket.onopen(),
            socket,
            retries,
            delay,
            backOffCoefficient,
        })
    } else analytics.init(token)

    return trackerMiddleWare({ mapper, analytics, onPreProcess })
}

export const combineAnalytics = (...analytics) => analytics.reduce((accum, analytic) => ({ ...accum, ...analytic }), {})

/**
 * EventTypes semantics:
 *
 * EventTypes.identify: who is the customer?
 * EventTypes.page: what web page are they on?
 * EventTypes.track: what are they doing?
 * EventTypes.group: what account or organization are they part of?
 * EventTypes.alias: what was their past identity?
 */
const EventTypes = {
    identify: 'identify',
    page: 'page',
    track: 'track',
    group: 'group',
    alias: 'alias',
}
